/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { useCallback, useEffect, useRef } from "react"
import { css, jsx } from "@emotion/core"
import { Form } from "grab-ui"
import { Checkbox, Row } from "@duxton-web/ui"
import { BULK_UPLOAD_EXPORT_FILE_FIELDS } from "constants/menu_bulk_upload"
import { useTranslations } from "common/utils/i18n"
import { ExportMenuItemProperty } from "types/delv-platform-api/inventoryCategory"
import {
  checkAllOption,
  checkAllRowWrap,
  checkboxGroupCss,
  checkboxGroupWrapCss,
  selectionWrapCss,
  textCommonCss,
} from "./styles"
import { useInventoryCategoryCheckAllBehavior } from "../hooks/useInventoryCategoryCheckAllBehavior"

const menuItemProperty = ["basic_info", "categories", "price", "option_groups"]

const MenuItemPropertySelect = ({ form }: FormComponentProps) => {
  const [allPropertyText] = useTranslations([
    "bulkUpload.drawers.downloadItem.property.allProperty",
  ])

  const { getFieldDecorator } = form

  const selectableProperty = menuItemProperty.map(property => property)
  const selectedProperty = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.menuItemProperty,
  )

  const isSelectedAllCategories =
    !!selectedProperty?.length &&
    selectedProperty.length === selectableProperty.length

  const doCheckAll = useCallback(() => {
    form.setFieldsValue({
      [BULK_UPLOAD_EXPORT_FILE_FIELDS.menuItemProperty]: selectableProperty,
    })
  }, [form, selectableProperty])

  const doUncheckAll = () => {
    form.setFieldsValue({
      [BULK_UPLOAD_EXPORT_FILE_FIELDS.menuItemProperty]: [],
    })
  }

  const {
    onCheckAllChange,
    checkAllChecked,
    checkAllIndeterminate,
  } = useInventoryCategoryCheckAllBehavior({
    doCheckAll,
    doUncheckAll,
    isSelectedAllCategories,
    selectedCategoryIDs: selectedProperty,
  })

  const propertyStatusText = useTranslations(
    Object.keys(ExportMenuItemProperty).map(
      key => `bulkUpload.drawers.downloadItem.property.${key}`,
    ),
  )

  const propertyStatusDesc = useTranslations(
    Object.keys(ExportMenuItemProperty).map(
      key => `bulkUpload.drawers.downloadItem.property.${key}_DESC`,
    ),
  )

  const propertyOptions = Object.keys(ExportMenuItemProperty).map(
    (property: keyof typeof ExportMenuItemProperty, index) => ({
      label: (
        <div
          key={property}
          css={css`
            ${textCommonCss};
            font-size: 14px;
          `}
        >
          {propertyStatusText[index]}
          <div
            css={css`
              color: #707070;
            `}
          >
            {propertyStatusDesc[index]}
          </div>
        </div>
      ),
      value: ExportMenuItemProperty[property],
    }),
  )

  const doneFirstDoCheckAll = useRef(false)

  useEffect(() => {
    if (!doneFirstDoCheckAll.current) {
      doneFirstDoCheckAll.current = true
      doCheckAll()
    }
  }, [doCheckAll])

  return (
    <div css={selectionWrapCss}>
      <Row align="middle" justify="space-between" css={checkAllRowWrap}>
        <Checkbox
          css={checkAllOption}
          checked={checkAllChecked}
          indeterminate={checkAllIndeterminate}
          onChange={onCheckAllChange}
        >
          <div
            css={css`
              ${textCommonCss};
              font-weight: 500;
            `}
          >
            {allPropertyText}
          </div>
        </Checkbox>
      </Row>
      <div css={checkboxGroupWrapCss}>
        <Form.Item>
          {getFieldDecorator(BULK_UPLOAD_EXPORT_FILE_FIELDS.menuItemProperty)(
            <Checkbox.Group css={checkboxGroupCss} options={propertyOptions} />,
          )}
        </Form.Item>
      </div>
    </div>
  )
}

export default MenuItemPropertySelect
